import React, { Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import styled from 'src/ui/library/styled';
import styles from './styles';
import DOMPurify from 'dompurify';
import ErrorIcon from '@material-ui/icons/Error';

class TextFieldBase extends React.Component {
  static propTypes = {
    /** Classname to be applied to <TextField> */
    className: PropTypes.string,
    /** If `true`, the input will not have an underline. */
    disableUnderline: PropTypes.bool,
    /** If `true`, password will be visible */
    showPassword: PropTypes.bool,
    /** If `true`, textfield will be in error state */
    hasError: PropTypes.bool,
    /** error text shown in error state */
    errorText: PropTypes.string,
    /** Override or extend default styles */
    styles: PropTypes.object,
    /** Name attribute of the `input` element. */
    name: PropTypes.string,
    /** Value of `input` element */
    value: PropTypes.string,
    /** Type of the input element. It should be a valid HTML5 input type. */
    type: PropTypes.string,
    /** Text used for input label */
    label: PropTypes.string,
    /** If `true`, the label is displayed as required. */
    required: PropTypes.any,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,
    /** Shorten label with ellipsis */
    useEllipsisLabel: PropTypes.bool,
    /** reduce width of label if textfield has icon */
    ellipsisAdjustIcon: PropTypes.bool,
  };

  static defaultProps = {
    styles: {},
    hasError: false,
    errorText: '',
    disableUnderline: true,
    showPassword: false,
    useEllipsisLabel: false,
    ellipsisAdjustIcon: false,
  };

  state = {
    name: this.props.name,
    value: this.props.value || '',
    focused: this.props.focused || false,
    autofillFocus: false,
    passwordVisible: this.props.showPassword || false,
    event: { target: { value: '' } },
    label:this.props.label
  };

  componentDidMount() {
    if (this.inputRef) {
      // if (this.state.autofillFocus === false){
      //   setTimeout(()=>{
      //     this.setState({ autofillFocus: true });
      //   }, 10000)
      // }
    }
  }

  componentWillUnmount() {
    if (this.inputRef) {
      this.inputRef.removeEventListener('animationstart', this.onAnimationStart, false);
    }
  }

  // Chrome when using autofill looks really nasty. The only way around this is to
  // bind an animation event and listen for it. Once it triggers, auto focus the TextField
  onAnimationStart = ({ target, animationName }) => {
    if (animationName === 'onAutoFillStart') {
      setTimeout(this.setState({ autofillFocus: true }), 100);
    }
  };

  handleOnChange = evt => {
    if (evt && evt.target) {
      this.setState({ value: evt.target.value });
      let value = evt.target.value;
      this.setState({
        event: { target: { value: value } },
      });
    }

    return (this.props.onChange && this.props.onChange(evt)) || true;
  };

  handleOnFocus = (status, event) => {
    if ((this.props.isCallPersist || this.props.reloadPage) && !status) {
      if (event === true || event === false) {
        event = this.state.event;
        event.type = 'blur';
        if (event.target.value === '') {
          event.target.value = this.state.value;
        }
      }
      this.handleOnChange(event);
    }
    this.setState({ focused: status });
  };

  handleMouseDownPassword = event => {
    event.preventDefault();
  };

  handleClickShowPassword = () => {
    this.setState({ passwordVisible: !this.state.passwordVisible });
  };

  setInputRef = el => {
    if (typeof this.props.inputRef === 'function') {
      this.props.inputRef(el);
    }

    this.inputRef = findDOMNode(el);
  };

  render() {
    const {
      classes,
      helperText,
      styles,
      type,
      hasError,
      errorText,
      className,
      showPassword,
      onChange,
      onFocus,
      disableUnderline,
      inputRef,
      InputLabelProps,
      InputProps,
      useEllipsisLabel,
      ellipsisAdjustIcon,
      forceShrink,
      forceFocus,
      label,
      ...otherProps
    } = this.props;

    const textClassName = classNames(
      {
        [classes.textField]: true,
        [classes.textFieldFocus]: this.state.focused,
        [classes.hasError]: this.props.hasError,
        [classes.enableTextField]: !this.props.disabled,
      },
      className,
    );

    let inputType = type;

    if (inputType === 'password' && this.state.passwordVisible) inputType = 'text';
    return (
      <Fragment>
        <TextField
          helperText={helperText}
          type={inputType}
          className={textClassName}
          onChange={this.handleOnChange}
          onFocus={this.handleOnFocus.bind(null, true)}
          onBlur={this.handleOnFocus.bind(null, false)}
          fullWidth={true}
          inputRef={el => this.setInputRef(el)}
          InputProps={{
            disableUnderline,
            className: classes.inputRoot,
            classes: {
              input: classes.inputDom,
            },
            endAdornment:
              this.props.type === 'password' ? (
                <InputAdornment position="end" className={classes.visibilityIcon}>
                  <IconButton
                    aria-label="Toggle password visibility"
                    onClick={this.handleClickShowPassword}
                    onMouseDown={this.handleMouseDownPassword}>
                    {this.state.passwordVisible ? (
                      <VisibilityOff className={classNames(classes.visibilityIconRoot, classes.showPassword)} />
                    ) : (
                      <Visibility className={classes.visibilityIconRoot} />
                    )}
                  </IconButton>
                </InputAdornment>
              ) : null,
            ...InputProps,
          }}
          InputLabelProps={{
            ...(this.state.autofillFocus || forceFocus ? { focused: true } : {}),
            ...(this.state.autofillFocus || forceShrink ? { shrink: true } : {}),
            classes: {
              formControl: useEllipsisLabel
                ? ellipsisAdjustIcon
                  ? classNames(classes.formLabelEllipsisWithIcon, classes.formLabelEllipsis)
                  : classes.formLabelEllipsis
                : classes.formControl,
              shrink: useEllipsisLabel ? classes.shrinkEllipsis : classes.shrink,
            },
            FormLabelClasses: {
              root: classes.formLabel,
              focused: classes.focused,
              asterisk: this.props.classes.labelAsterisk,
            },
            ...InputLabelProps,
          }}
          FormHelperTextProps={{
            classes: {
              root: classes.formHelperText,
            },
            error: hasError,
          }}
          value={this.state.value}
          label={ this.props.required ? label+ intl.get('questionnaire.requiredLabel') : label }
          {...otherProps}
        />
        {!this.props.helpText && hasError && errorText && (
          <p className="textfield-error-text"> <ErrorIcon sx={{ verticalAlign:'bottom', fontSize: 14 }}/> <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(errorText) }} /></p>
        )}
         {(this.state.value === null || this.state.value === '')  && this.state.label === 'CCH Locator ID' && (
          <p className="textfield-error-text" style ={{color: "#ff0000"}}> <ErrorIcon style={{ verticalAlign:'bottom', fontSize: '16px' }}/> <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize('Locator ID do not exist') }} /></p>
          // <p className="textfield-error-text" style ={{color: "#ff0000"}} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize('Locator ID do not exist') }} />
        )}
      </Fragment>
    );
  }
}

export { TextFieldBase };
export default styled(TextFieldBase)(styles);



// WEBPACK FOOTER //
// ./src/ui/library/TextField/TextFieldBase.js